import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Button, Card, CardContent, CardActions,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, IconButton, Collapse,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useIntl } from "react-intl";
import { useAuth } from "../../providers/AuthProvider";
import { useNotification } from "../../providers/NotificationProvider";
import DraggablePaper from "../../components/common/DraggablePaper";
import {
  fetchAwarenesses, createAwareness, updateAwareness, deleteAwareness,
} from "../../api/memberApi";

export default function AwarenessListPage() {
  const intl = useIntl();
  const { companyId, memberId } = useAuth();
  const notify = useNotification();
  const t = (id, vals) => intl.formatMessage({ id }, vals);

  const [awarenesses, setAwarenesses] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [awarenessName, setAwarenessName] = useState("");
  const [awarenessDescription, setAwarenessDescription] = useState("");
  const [awarenessUrl, setAwarenessUrl] = useState("");

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [expandedIds, setExpandedIds] = useState({});

  const loadAwarenesses = useCallback(async () => {
    if (!memberId) return;
    setLoading(true);
    try {
      const data = await fetchAwarenesses(memberId);
      setAwarenesses(Array.isArray(data) ? data : []);
    } catch (e) {
      notify.apiError(e);
    } finally {
      setLoading(false);
    }
  }, [memberId, notify]);

  useEffect(() => { loadAwarenesses(); }, [loadAwarenesses]);

  const openDialog = (awareness = null) => {
    setEditTarget(awareness);
    setAwarenessName(awareness?.awarenessName || "");
    setAwarenessDescription(awareness?.awarenessDescription || "");
    setAwarenessUrl(awareness?.url || "");
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!awarenessName.trim()) { notify.error(t("awareness.name.required")); return; }
    try {
      if (editTarget) {
        await updateAwareness({ awarenessId: editTarget.awarenessId, companyId, memberId, awarenessName: awarenessName.trim(), awarenessDescription: awarenessDescription.trim(), url: awarenessUrl.trim() });
        notify.success(t("awareness.updateSuccess"));
      } else {
        await createAwareness({ companyId, memberId, awarenessName: awarenessName.trim(), awarenessDescription: awarenessDescription.trim(), url: awarenessUrl.trim() });
        notify.success(t("awareness.createSuccess"));
      }
      setDialogOpen(false);
      loadAwarenesses();
    } catch (e) { notify.apiError(e); }
  };

  const handleDelete = async () => {
    try {
      await deleteAwareness(deleteTarget.awarenessId);
      notify.success(t("awareness.deleteSuccess"));
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      loadAwarenesses();
    } catch (e) { notify.apiError(e); }
  };

  const toggleExpand = (id) =>
    setExpandedIds((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>{t("menu.awareness")}</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDialog()}>
          {t("awareness.add.button")}
        </Button>
      </Box>

      {loading ? (
        <Typography color="text.secondary">{t("common.loading")}</Typography>
      ) : awarenesses.length === 0 ? (
        <Typography color="text.secondary">{t("awareness.empty")}</Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {awarenesses.map((aw) => (
            <Card key={aw.awarenessId} variant="outlined">
              <CardContent sx={{ pb: 1 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>{aw.awarenessName}</Typography>
                  <IconButton size="small" onClick={() => toggleExpand(aw.awarenessId)}>
                    {expandedIds[aw.awarenessId] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
                <Collapse in={!!expandedIds[aw.awarenessId]}>
                  <Box sx={{ mt: 1 }}>
                    {aw.awarenessDescription && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, whiteSpace: "pre-wrap" }}>
                        {aw.awarenessDescription}
                      </Typography>
                    )}
                    {aw.url && (
                      <Typography variant="body2">
                        <a href={aw.url} target="_blank" rel="noopener noreferrer">{aw.url}</a>
                      </Typography>
                    )}
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 0.5 }}>
                      {aw.createdAt && (
                        <Typography variant="caption" color="text.secondary">
                          {t("common.createdAt")}: {new Date(aw.createdAt).toLocaleString("ja-JP")}
                        </Typography>
                      )}
                      {aw.updatedAt && aw.updatedAt !== aw.createdAt && (
                        <Typography variant="caption" color="text.secondary">
                          {t("common.updatedAt")}: {new Date(aw.updatedAt).toLocaleString("ja-JP")}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Collapse>
              </CardContent>
              <CardActions sx={{ pt: 0, gap: 0.5 }}>
                <Button size="small" startIcon={<EditIcon />} onClick={() => openDialog(aw)}>{t("common.edit")}</Button>
                <Button size="small" color="error" startIcon={<DeleteIcon />}
                  onClick={() => { setDeleteTarget(aw); setDeleteDialogOpen(true); }}>
                  {t("common.delete")}
                </Button>
              </CardActions>
            </Card>
          ))}
        </Box>
      )}

      {/* 追加・編集ダイアログ */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>{editTarget ? t("awareness.edit.title") : t("awareness.add.title")}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField label={t("awareness.name")} required fullWidth value={awarenessName} onChange={(e) => setAwarenessName(e.target.value)} />
            <TextField label={t("awareness.description")} fullWidth multiline rows={3} value={awarenessDescription} onChange={(e) => setAwarenessDescription(e.target.value)} />
            <TextField label={t("awareness.url")} fullWidth value={awarenessUrl} onChange={(e) => setAwarenessUrl(e.target.value)} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={handleSave}>{t("common.save")}</Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認 */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>{t("awareness.deleteConfirm.title")}</DialogTitle>
        <DialogContent>
          <Typography>{t("awareness.deleteConfirm.message", { name: deleteTarget?.awarenessName || "" })}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>{t("common.delete")}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
